import React, { useCallback, useEffect, useState } from 'react';
import ContentLayout from '@layouts/ContentLayout';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearKycDetails,
  clearAllKycs,
  getKycDetailsAndDocs,
  rejectEFR,
  syncProviderKyc
} from '@store/actions/kycActions';
import { KYC_DOC_STATUS, KYC_PROVIDER, KYC_STATUS, KYC_V2_STATUS, PRODUCT, ROLES } from '@constants';
import { getKycV2StatusColor, getVerificationTitle } from '@utils';
import { Link as ReactLink, useParams } from 'react-router-dom';
import { RxDashboard } from 'react-icons/rx';
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Link,
  Spinner,
  StackDivider,
  Tag,
  Text,
  useDisclosure,
  VStack,
  Wrap
} from '@chakra-ui/react';
import KycDocCard from '@components/Kyc/KycDocCard';
import { BsArrowRight } from 'react-icons/bs';
import ModalLayout from '@components/Modal/ModalLayout';
import JsonViewer from '@components/Modal/JsonViewer';
import KycUpdateModal from '@components/Modal/KycUpdateModal';
import { setAlert } from '@store/actions/alertActions';
import { clearUserDetails, getUserById, updateUserKycDetailsList, blockUser } from '@store/actions/usersActions';
import useIsMobile from '@hooks/useIsMobile';
import UserSideCard from '@components/UserSideCard';
import { AiOutlineSync } from 'react-icons/ai';
import CopyableText from '@components/CopyableText';
import useAuthRole from '@hooks/useAuthRole';
import usePromise from '@hooks/usePromise';
import { getOcrDetails } from '@store/actions/nronboardActions';
import { updateOcrByDoc } from '@store/actions/workFlowsActionsV2';
import ReKycOcrModal from '@components/Kyc/ReKycOcrModal';
import VerifyAllDocsModal from '@components/Kyc/VerifyAllDocsModal';
import { getProviderDashboardConfigOrNull, isAllowedKycProviderForSync } from './kycUiUtils';
import UserKycStatus from '@components/Kyc/UserKycStatus';
import ConfirmModal from '@components/Modal/ConfirmModal';
import { formatDate } from '@utils/dateUtils';
import { MdBlock } from 'react-icons/md';


const KycDocs = () => {
  // Context and hooks
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { hasAnyAuthority } = useAuthRole();
  const { userId, product, provider, subProvider } = useParams();

// Redux selectors
  const { data: kycDetails, isLoading, error } = useSelector((state) => state.kyc.user.kycDetails);
  const { data: userDetails } = useSelector((state) => state.users.userDetails);
  const { providerKyc : providerKycDetails, kycSteps : docs  } = kycDetails || {};
  const rejectionReasons = providerKycDetails?.rejectionReasons;

// Local state
  const [isSyncLoading, setIsSyncLoading] = useState(false);
  const [isVerifyAllDocsLoading, setIsVerifyAllDocsLoading] = useState(false);
  const [tags, setTags] = useState([]);

  const [activeDoc, setActiveDoc] = useState(null);
  const [statusUpdateData, setStatusUpdateData] = useState(null);
  const [activeRemarks, setActiveRemarks] = useState(null);
  const [activeRejectReason, setActiveRejectReason] = useState(null);

  const [rjctBtnLoading, setRjctBtnLoading] = useState(false);
  const [passportFrontOcr, setPassportFrontOcr] = useState({});
  const [ocrData, setOcrData] = useState({});
  const [isORKycBtnLoading, setORKycBtnLoading] = useState(false);
  const [isDataReq, setDataReq] = useState(false);

// Disclosure hooks — General
  const {
    isOpen: isKycInfoOpen,
    onOpen: onKycInfoOpen,
    onClose: onKycInfoClose,
  } = useDisclosure();

  const {
    isOpen: isUpdateKycStatusOpen,
    onOpen: onUpdateKycStatusOpen,
    onClose: onUpdateKycStatusClose,
  } = useDisclosure();

  const {
    isOpen: isRemarksOpen,
    onOpen: onRemarksOpen,
    onClose: onRemarksClose,
  } = useDisclosure();

  const {
    isOpen: isRejectReasonOpen,
    onOpen: onRejectReasonOpen,
    onClose: onRejectReasonClose,
  } = useDisclosure();

  const {
    isOpen: isEFRRejectConfirmationOpen,
    onOpen: onEFRRejectConfirmationOpen,
    onClose: onEFRRejectConfirmationClose,
  } = useDisclosure();

  const {
    isOpen: isBlockModalOpen,
    onOpen: onBlockModalOpen,
    onClose: onBlockModalClose
  } = useDisclosure();

  const { isLoading: blockBtnLoading, run: runBlockUser } = usePromise();

// Disclosure hooks — Provider Specific (e.g., Saber, OnRamp, Encryptus)
  const {
    isOpen: isSaberVerifyOpen,
    onOpen: onSaberVerifyOpen,
    onClose: onSaberVerifyClose,
  } = useDisclosure();

  const {
    isOpen: isOnRampMoneyVerifyOpen,
    onOpen: onRampMoneyVerifyOpen,
    onClose: onRampMoneyVerifyClose,
  } = useDisclosure();

  const {
    isOpen: isEncryptusVerifyOpen,
    onOpen: onEncryptusVerifyOpen,
    onClose: onEncryptusVerifyClose,
  } = useDisclosure();

// Disclosure hook — OnRampMoney-specific for verifying all docs
  const {
    isOpen: isVerifyAllDocsOpen,
    onOpen: onVerifyAllDocsOpen,
    onClose: onVerifyAllDocsClose,
  } = useDisclosure();

  useEffect(() => {
    if (userId && product && provider) {
      triggerGetUserDetails(userId);
      triggerGetKycDetailsAndDocs();

      // OCR logic
      if (provider === KYC_PROVIDER.SABER && product === PRODUCT.REMITTANCE) {
        getUserOcrDetails({ setPassportFrontOnly: true });
      }
      if (provider === KYC_PROVIDER.ON_RAMP_MONEY) {
        getUserOcrDetails({ setPassportFrontOnly: false });
      }
      if (provider === KYC_PROVIDER.ENCRYPTUS) {
        getUserOcrDetails({ setPassportFrontOnly: false });
      }
    }

    return () => {
      dispatch(clearKycDetails());
      dispatch(clearUserDetails());
    };
  }, [userId, product, provider, subProvider]);

  useEffect(() => {
    if (activeDoc) onKycInfoOpen();
    else onKycInfoClose();
  }, [activeDoc]);

  useEffect(() => {
    if (statusUpdateData) onUpdateKycStatusOpen();
    else onUpdateKycStatusClose();
  }, [statusUpdateData]);

  useEffect(() => {
    if (providerKycDetails?.tags) {
      setTags(providerKycDetails.tags);
    }
  }, [providerKycDetails]);

  useEffect(() => {
    if (activeRemarks) onRemarksOpen();
    else onRemarksClose();
  }, [activeRemarks]);

  useEffect(() => {
    if (activeRejectReason) onRejectReasonOpen();
    else onRejectReasonClose();
  }, [activeRejectReason]);

  const handleKycInfoClick = (kyc, isDataReq) => {
    setActiveDoc(kyc);
    if (isDataReq) setDataReq(true);
    else setDataReq(false);
  };

  const onKycDocActionClick = ({ kyc, newStatus }) => {
    setStatusUpdateData({
      userId,
      status: newStatus,
      kycDetailsId: kyc.id,
      kycType: kyc.kycType,
      remarks: kyc.remarks || '',
      subDocumentType: kyc.subDocumentType,
      rejectionReason: kyc?.rejectionReason || '',
      isReject: KYC_DOC_STATUS.REJECTED === newStatus,
      provider
    });
  };
  const handleRemarksClick = (kyc) => {
    setActiveRemarks(kyc);
  };

  const handleRemarksClose = () => {
    setActiveRemarks(null);
  };

  const handleKycInfoClose = () => {
    setActiveDoc(null);
  };

  const handleUpdateKycClose = () => {
    setStatusUpdateData(null);
  };

  const handleRejectReasonClick = (kyc) => {
    setActiveRejectReason(kyc);
  };

  const handleActiveRejectReasonClose = () => {
    setActiveRejectReason(null);
  };

  const triggerGetKycDetailsAndDocs = useCallback(() => {
    const payload = {
      product,
      provider,
      subProvider
    };

    dispatch(
      getKycDetailsAndDocs({
        userId,
        payload
      })
    );
  }, [
    dispatch,
    userId,
    product,
    provider,
    subProvider
  ]);

  const triggerGetUserDetails = useCallback(
    (userId) => {
      dispatch(getUserById(userId));
    },
    [dispatch]
  );

  const triggerSyncKyc = useCallback(
    ({ userId, product, provider, subProvider }) => {
      setIsSyncLoading(true);
      dispatch(
        syncProviderKyc({
          userId,
          product,
          provider,
          subProvider,
          onFinish: () => setIsSyncLoading(false)
        })
      );
    },
    [dispatch]
  );

  const handleSyncButtonClick = useCallback(() => {
    triggerSyncKyc({
      userId,
      product: providerKycDetails.product,
      provider: providerKycDetails.kycProvider,
      subProvider: providerKycDetails.subProvider
    });
  }, [userId, providerKycDetails, triggerSyncKyc]);

  const onKycDocUpdateSuccess = useCallback(
    ({ status }) => {
      triggerGetKycDetailsAndDocs();
      dispatch(clearAllKycs());
      dispatch(
        setAlert({
          title: 'Kyc Doc Status updated to ' + status,
          status: 'success'
        })
      );
    },
    [
      dispatch,
      triggerGetKycDetailsAndDocs
    ]
  );

  const onCreateEKycSuccess = useCallback(() => {
    triggerGetKycDetailsAndDocs();
  }, [dispatch, triggerGetKycDetailsAndDocs]);

  const onCreateMKycSuccess = useCallback(() => {
    triggerGetKycDetailsAndDocs()
  }, [dispatch, triggerGetKycDetailsAndDocs]);

  const getKycInfoStr = useCallback(
    (kycInfo) =>
      kycInfo
        ?.replaceAll(`'`, `\"`)
        .replaceAll('True', 'true')
        .replaceAll('False', 'false'),
    []
  );

  const handleDashboardLinkClick = useCallback(
    () =>
      window.open(
        getProviderDashboardConfigOrNull(provider).url.replace(
          '{clientId}',
          providerKycDetails?.providerClientId
        )
      ),
    [providerKycDetails]
  );

  const getUserOcrDetails = ({ setPassportFrontOnly = true }) => {
    dispatch(
      getOcrDetails({
        userId,
        isNronboarding: false,
        onComplete: () => {},
        onSuccess: (data) => {
          const { ocrData } = data;
          const { passportFront } = ocrData;
          setPassportFrontOnly && setPassportFrontOcr(passportFront?.ocrData);
          !setPassportFrontOnly && setOcrData(ocrData);
        }
      })
    );
  };

  const handleVerifyClickV2 = ({ provider, payload }) => {
    if (provider === KYC_PROVIDER.ENCRYPTUS) {
      handleVerifyModalOpen(provider);
      return;
    }

    if (provider === KYC_PROVIDER.SABER) {
      handleVerifyModalOpen(provider);
      return;
    }

    if (
      provider === KYC_PROVIDER.ON_RAMP_MONEY &&
      payload?.kyc?.kycType === 'ON_RAMP_KYC_DATA'
    ) {
      handleVerifyModalOpen(provider);
      return;
    }

    onKycDocActionClick(payload);
  };

  const handleVerifyModalOpen = (kycProvider) => {
    switch (kycProvider) {
      case KYC_PROVIDER.ON_RAMP_MONEY:
        onRampMoneyVerifyOpen();
        return;
      case KYC_PROVIDER.SABER:
        onSaberVerifyOpen();
        return;
      case KYC_PROVIDER.ENCRYPTUS:
        onEncryptusVerifyOpen();
        return;
      default:
        return;
    }
  };

  const handleVerifyModalClose = (kycProvider) => {
    switch (kycProvider) {
      case KYC_PROVIDER.ENCRYPTUS:
        onEncryptusVerifyClose();
        return;
      case KYC_PROVIDER.ON_RAMP_MONEY:
        onRampMoneyVerifyClose();
        return;
      case KYC_PROVIDER.SABER:
        onSaberVerifyClose();
        return;
      default:
        return;
    }
  };

  const handleUpdateOcrSubmit = (data) => {
    const payload = {
      ocrData: {
        ...data
      }
    };
    setORKycBtnLoading(true);
    dispatch(
      updateOcrByDoc({
        userId,
        payload,
        onSuccess: () => {
          triggerGetKycDetailsAndDocs();
          handleVerifyModalClose(provider);
        },
        onComplete: () => {
          setORKycBtnLoading(false);
        }
      })
    );
  };

  const handleTriggerOCRClick = () => {
    dispatch(
      setAlert({
        title: 'Verification success',
        status: 'success'
      })
    );
    handleVerifyModalClose(provider);
  };

  const handleVerifyAllDocsSubmit = (docs) => {
    setIsVerifyAllDocsLoading(true);
    dispatch(
      updateUserKycDetailsList({
        userId,
        details: docs,
        onSuccess: () => {
          triggerGetKycDetailsAndDocs()
          onVerifyAllDocsClose();
        },
        onComplete: () => {
          setIsVerifyAllDocsLoading(false);
        }
      })
    );
  };

  const showVerifyDocsBtn = (docs) => {
    if (!docs) return false;
    if (docs.length === 0) return false;
    const docsSentToProvider = docs?.filter(
      (doc) => doc.status === 'SENT_TO_PROVIDER'
    );
    if (docsSentToProvider.length > 0) return false;
    const nonTerminalStateDocs = docs?.filter(
      (doc) => !['VERIFIED', 'COMPLETED'].includes(doc.status)
    );
    return nonTerminalStateDocs.length !== 0;

  };

  const handleRejectEFR = () => {
    setRjctBtnLoading(true);
    dispatch(
      rejectEFR({
        userId,
        onSuccess: () => {
          triggerGetKycDetailsAndDocs();
        },
        onComplete: () => {
          setRjctBtnLoading(false);
          onEFRRejectConfirmationClose();
        }
      })
    );
  };

  const handleBlockClick = () => {
    onBlockModalOpen();
  };

  const handleBlockModalClose = () => {
    onBlockModalClose();
  };

  const handleBlockModalConfirm = () => {
    runBlockUser(
      dispatch(
        blockUser({
          userId,
          product,
          provider,
          subProvider,
          onSuccess: () => {
            triggerGetUserDetails(userId);
            triggerGetKycDetailsAndDocs();
          },
          onComplete: () => {
            handleBlockModalClose();
          }
        })
      )
    );
  };


  return (
    <Box position={'relative'}>
      <ModalLayout isOpen={isEFRRejectConfirmationOpen} onClose={onEFRRejectConfirmationClose}>
        <ConfirmModal
          handleSubmit={handleRejectEFR}
          isLoading={rjctBtnLoading}
          prompt={`Are you sure you want to cancel this users EFR KYC`}
        />
      </ModalLayout>
      <ModalLayout isOpen={isBlockModalOpen} onClose={handleBlockModalClose}>
        <ConfirmModal
          prompt={'Are you sure you want to block this user?'}
          handleSubmit={handleBlockModalConfirm}
          isLoading={blockBtnLoading}
          handleCancel={handleBlockModalClose}
        />
      </ModalLayout>
      <ModalLayout
        isOpen={isEncryptusVerifyOpen}
        onClose={() => handleVerifyModalClose(provider)}
      >
        <ReKycOcrModal
          kycProvider={KYC_PROVIDER.ENCRYPTUS}
          ocrData={ocrData}
          doc={'PASSPORT_FRONT'}
          onCancel={() => handleVerifyModalClose(provider)}
          isLoading={isORKycBtnLoading}
          onSubmit={handleUpdateOcrSubmit}
          onTriggerOcr={handleTriggerOCRClick}
        />
      </ModalLayout>
      <ModalLayout
        isOpen={isOnRampMoneyVerifyOpen}
        onClose={() => handleVerifyModalClose(provider)}
      >
        <ReKycOcrModal
          kycProvider={KYC_PROVIDER.ON_RAMP_MONEY}
          ocrData={ocrData}
          doc={'PASSPORT_FRONT'}
          onCancel={() => handleVerifyModalClose(provider)}
          onSubmit={handleUpdateOcrSubmit}
          isLoading={isORKycBtnLoading}
          onTriggerOcr={handleTriggerOCRClick}
        />
      </ModalLayout>
      <ModalLayout
        isOpen={isSaberVerifyOpen}
        onClose={() => handleVerifyModalClose(provider)}
      >
        <ReKycOcrModal
          ocrData={ocrData}
          kycProvider={KYC_PROVIDER.SABER}
          doc={'PASSPORT_FRONT'}
          onCancel={() => handleVerifyModalClose(provider)}
          onSubmit={handleUpdateOcrSubmit}
          isLoading={isORKycBtnLoading}
          onTriggerOcr={handleTriggerOCRClick}
        />
      </ModalLayout>
      {provider === KYC_PROVIDER.ON_RAMP_MONEY && (
        <ModalLayout
          size="5xl"
          isOpen={isVerifyAllDocsOpen}
          onClose={onVerifyAllDocsClose}
        >
          <VerifyAllDocsModal
            isLoading={isVerifyAllDocsLoading}
            onCancel={onVerifyAllDocsClose}
            onSubmit={handleVerifyAllDocsSubmit}
            docs={docs?.filter(
              (doc) =>
                doc?.kycType === 'PROOF_OF_IDENTITY' &&
                !['VERIFIED', 'REJECTED'].includes(doc.status)
            )}
          />
        </ModalLayout>
      )}

      {activeDoc && (
        <ModalLayout
          isOpen={isKycInfoOpen}
          onClose={handleKycInfoClose}
          size={'lg'}
        >
          <JsonViewer
            title={!isDataReq ? 'KYC Info' : 'KYC Data Req'}
            subtitle={activeDoc?.kycType}
            jsonString={getKycInfoStr(activeDoc?.kycInfo)}
          />
        </ModalLayout>
      )}
      {statusUpdateData && (
        <ModalLayout
          isOpen={isUpdateKycStatusOpen}
          onClose={handleUpdateKycClose}
        >
          <KycUpdateModal
            onClose={handleUpdateKycClose}
            statusUpdateData={statusUpdateData}
            onUpdateSuccess={onKycDocUpdateSuccess}
          />
        </ModalLayout>
      )}
      {activeRemarks && (
        <ModalLayout
          isOpen={isRemarksOpen}
          onClose={handleRemarksClose}
          size={'lg'}
        >
          <JsonViewer
            title={'KYC remarks'}
            subtitle={activeRemarks?.kycType}
            jsonString={activeRemarks?.remarks}
          />
        </ModalLayout>
      )}
      {activeRejectReason && (
        <ModalLayout
          isOpen={isRejectReasonOpen}
          onClose={handleActiveRejectReasonClose}
          size="lg"
        >
          <JsonViewer
            title={'KYC Reject reason'}
            jsonString={activeRejectReason?.rejectionReason}
          />
        </ModalLayout>
      )}
      <ContentLayout>
        <Grid gridTemplateColumns={{ base: '1fr', lg: '7fr 0fr 2fr' }}>
          <VStack
            p={10}
            gap={4}
            overflowY={{ base: 'initial', lg: 'scroll' }}
            overflowX={'hidden'}
            h={{ lg: '90vh' }}
            px={{ base: 4, sm: 10 }}
            alignItems={'stretch'}
            className="scroll"
          >
            <VStack w={'100%'} gap={1} alignItems={'flex-start'}>
              <Flex
                w={'100%'}
                alignItems={'baseline'}
                justifyContent={'space-between'}
              >
                <Heading color={'colorPrimary'}>
                  {getVerificationTitle(product, provider)} Details
                </Heading>
                <HStack gap={2}>
                  {hasAnyAuthority(ROLES.ADMIN_ROLE, ROLES.MANAGER_ROLE, ROLES.BLOCK_USER_ACCESS) && (
                    <Button
                      colorScheme={'orange'}
                      size="sm"
                      bg="backGround"
                      border={'2px solid'}
                      borderColor={'whiteAlpha.300'}
                      borderRadius={'xl'}
                      p={4}
                      isLoading={blockBtnLoading}
                      onClick={handleBlockClick}
                      gap={2}
                    >
                      <MdBlock />
                      Block User
                    </Button>
                  )}
                  {providerKycDetails?.status === 'VERIFIED' &&
                    provider === 'EFR' &&
                    hasAnyAuthority(ROLES.ADMIN_ROLE, ROLES.MANAGER_ROLE) && (
                      <Button
                        variant={'outline'}
                        colorScheme="red"
                        onClick={onEFRRejectConfirmationOpen}
                      >
                        Reject EFR
                      </Button>
                    )}
                </HStack>
              </Flex>
              <Grid
                gridTemplateColumns={{ base: '1fr', lg: '2fr 1.4fr 1fr' }}
                alignItems={'baseline'}
                w={'100%'}
                gap={2}
                width={'100%'}
                maxW={'800px'}
                justifyContent={'space-between'}
              >
                <VStack
                  width={'full'}
                  justifySelf={'flex-start'}
                  alignItems={'stretch'}
                >
                  <HStack>
                    <Text color={'gray'}>Status : </Text>
                    <HStack>
                      {providerKycDetails?.status && (
                        <Icon
                          viewBox="0 0 200 200"
                          color={getKycV2StatusColor(providerKycDetails?.status)}
                          w="8px"
                        >
                          <path
                            fill="currentColor"
                            d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
                          />
                        </Icon>
                      )}
                      <Text fontSize={{ base: 'sm', sm: 'md' }} color="white">
                        {providerKycDetails?.status || '-'}
                      </Text>
                      {isAllowedKycProviderForSync(provider) &&
                        (!isSyncLoading ? (
                          <AiOutlineSync
                            onClick={handleSyncButtonClick}
                            color={'white'}
                            cursor={'pointer'}
                          />
                        ) : (
                          <Spinner color={'white'} size={'sm'} />
                        ))}
                    </HStack>
                  </HStack>
                  <HStack>
                    <Text color={'gray'}>Updated At : </Text>
                    <HStack>
                      <Text
                        fontSize={{ base: 'sm', sm: 'md' }}
                        color="white"
                      >
                        {formatDate(providerKycDetails?.updatedAt) || '-'}
                      </Text>
                    </HStack>
                  </HStack>
                  <HStack>
                    <Text color={'gray'}>Client ID : </Text>
                    <HStack>
                      <CopyableText
                        fontSize={{ base: 'sm', sm: 'md' }}
                        color="white"
                      >
                        {providerKycDetails?.providerClientId || '-'}
                      </CopyableText>
                    </HStack>
                  </HStack>
                  <HStack align="center">
                    <Text color="gray">Rejection Reasons:</Text>
                    <HStack spacing={2}>
                      {Array.isArray(rejectionReasons) && rejectionReasons.length > 0 ? (
                        rejectionReasons.map((reason, index) => (
                          <CopyableText
                            key={index}
                            fontSize={{ base: 'sm', sm: 'md' }}
                            color="white"
                          >
                            <Tag size="md" variant="subtle" bgColor="red.300">
                              {reason}
                            </Tag>
                          </CopyableText>
                        ))
                      ) : (
                        <Text color="white">No rejection reason</Text>
                      )}
                    </HStack>
                  </HStack>
                  <HStack spacing={4} align="center">
                  <Text color={'gray'}>Tags : </Text>
                    <Wrap>
                      {tags?.length === 0 ? (
                        <Text>No tags</Text>
                      ) : (
                        tags?.map((tag, index) => (
                          <Tag key={index} size="md" variant="subtle" bgColor={'yellow.300'}>
                            {tag}
                          </Tag>
                        ))
                      )}
                    </Wrap>
                  </HStack>
                </VStack>
                {getProviderDashboardConfigOrNull(provider) !== null && (
                  <>
                    <HStack
                      color={'white'}
                      cursor={'pointer'}
                      _hover={{
                        textDecor: 'underline'
                      }}
                      onClick={handleDashboardLinkClick}
                    >
                      <Text>
                        {getProviderDashboardConfigOrNull(provider).text}
                      </Text>
                      <RxDashboard display={'inline'} />
                    </HStack>
                    <Link
                      as={ReactLink}
                      to={`/user/${userId}/kyc/checks`}
                      color="white"
                      justifyContent={'flex-start'}
                      justifySelf={'flex-end'}
                    >
                      <HStack>
                        <Text>View Checks</Text>
                        <BsArrowRight display={'inline'} />
                      </HStack>
                    </Link>
                  </>
                )}
              </Grid>
              {provider === KYC_PROVIDER.ON_RAMP_MONEY &&
                !(
                  providerKycDetails?.status === KYC_STATUS.REJECTED ||
                  providerKycDetails?.status === KYC_V2_STATUS.VERIFIED
                ) &&
                showVerifyDocsBtn(
                  docs
                    ? docs.filter((doc) => doc.kycType === 'PROOF_OF_IDENTITY')
                    : null
                ) && (
                  <>
                    <HStack width={'full'} justifyContent={'end'}>
                      <Button
                        colorScheme="brand"
                        onClick={() => {
                          onVerifyAllDocsOpen();
                        }}
                        variant={'outline'}
                      >
                        Verify Docs
                      </Button>
                    </HStack>
                  </>
                )}
            </VStack>

            <VStack
              alignSelf={'flex-start'}
              w={'100%'}
              alignItems={'flex-start'}
              gap={1}
            >
              { isLoading ? (
                <Flex justifyContent="center" alignItems="center" w="full" h="full">
                  <Spinner thickness="4px" size="lg" color="colorPrimary" />
                </Flex>
              ) : !!error ? (
                  <Box p={4} borderRadius="xl" bg={'backGround'} borderWidth="1px" borderColor={'whiteAlpha.300'}>
                    <Text color="white" fontSize="md">
                      {error}
                    </Text>
                  </Box>
              ) : docs?.length > 0 ?
                docs.map((item) => (
                  <KycDocCard
                    kycProvider={provider}
                    kycSubProvider={subProvider}
                    doc={item}
                    key={item.id}
                    onKycDocActionClick={onKycDocActionClick}
                    onKycInfoClick={(kyc) => {
                      handleKycInfoClick(kyc, false);
                    }}
                    onKycDataReqClick={(kyc) => {
                      handleKycInfoClick(kyc, true);
                    }}
                    userId={userId}
                    onRemarksClick={handleRemarksClick}
                    onRejectReasonClick={handleRejectReasonClick}
                    onVerifyClickV2={handleVerifyClickV2}
                  />
                )) : (
                  <Box
                    w={'full'}
                    p={4}
                    borderRadius="xl"
                    bg={'backGround'}
                    borderWidth="1px"
                    borderColor={'whiteAlpha.300'}
                  >
                    <Text color="white" fontSize="md">
                      This user doesn't have any KYC Docs yet.
                    </Text>
                  </Box>
                )
              }
            </VStack>
          </VStack>
          <Divider
            style={{ margin: '0' }}
            borderColor="whiteAlpha.300"
            orientation="vertical"
          ></Divider>
          {!isMobile && (
            <VStack
              alignItems={'start'}
              mx={'auto'}
              divider={
                <StackDivider
                  style={{ margin: '0' }}
                  borderColor="whiteAlpha.300"
                />
              }
              // gap={3}
            >
              <Box m={0} alignSelf={'flex-start'}>
                {isLoading ? (
                  <Spinner size={'lg'} color={'brand.200'} />
                ) : (
                  <UserSideCard
                    userDetails={userDetails}
                    onCreateEKycSuccess={onCreateEKycSuccess}
                    onCreateMKycSuccess={onCreateMKycSuccess}
                    showKycActions={provider === KYC_PROVIDER.COMPLY_CUBE}
                  />
                )}
              </Box>
              {/* Refactor the code and make it as component */}
              <Box m={0} alignSelf={'flex-start'}>
                <UserKycStatus
                  userId={userId}
                  product={product}
                  provider={provider}
                  isMobile={isMobile}
                />
              </Box>
            </VStack>
          )}
        </Grid>
      </ContentLayout>
    </Box>
  );
};

export default KycDocs;